import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminPanel } from '../../contexts/AdminPanelContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  Radio, Activity, AlertTriangle, RefreshCw, Loader2, BatteryWarning, Clock,
  SatelliteDish, CheckCircle2, Upload, Search, Signal, ScanLine, Plus,
  MousePointerClick, X, ChevronsUpDown, XCircle, Flag,
  Bluetooth, Crosshair, ArrowLeftRight, BatteryMedium,
} from 'lucide-react';
import { formatDT, getFileTimestamp } from '../../utils/timezone';
import { LocationPopover } from '../../components/shared/LocationPopover';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { reverseGeocodeBatch, GOOGLE_MAPS_API_KEY } from '../../utils/geocode';
import { downloadCsv } from '../../utils/csv';
import { DeviceBeaconsModal } from '../../components/admin/DeviceBeaconsModal';
import { BindingEngineSettingsWorkspace } from '../../components/admin/BindingEngineSettingsWorkspace';
import { RssiMeter } from '../../components/admin/RssiMeter';
import { BeaconsHelpPanel } from '../../components/admin/BeaconsHelp';
import { PaginationFooter, paginateArray, usePersistedPageSize } from '../../components/shared/PaginationFooter';
import { NOMINATIM_BASE } from '../../utils/nominatim';

const BINDING_STATUS_META = {
  bound: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', Icon: CheckCircle2 },
  eligible: { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', Icon: Clock },
  blocked: { cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icon: AlertTriangle },
  no_signal: { cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', Icon: XCircle },
};

// "Why (not) bound" reason line for a beacon inventory row.
const BindingStatusCell = ({ status, testId }) => {
  if (!status) return <span className="text-muted-foreground">-</span>;
  const meta = BINDING_STATUS_META[status.state] || BINDING_STATUS_META.blocked;
  const Icon = meta.Icon;
  return (
    <div className="max-w-[240px]" data-testid={testId}>
      <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`}>
        <Icon className="w-3 h-3 shrink-0" /> {status.label}
      </span>
      <p className="mt-0.5 text-[10px] text-muted-foreground leading-tight" title={status.reason}>{status.reason}</p>
    </div>
  );
};


const STALE_OPTIONS = [1, 3, 7, 14, 30];
const BATTERY_OPTIONS = [10, 15, 20, 30, 50];
const PAGE_LIMIT = 25;

const candidateDisplayName = (c) => {
  if (c.beacon_type === 'ibeacon') return `iBeacon ${c.beacon_major ?? '?'}-${c.beacon_minor ?? '?'}`;
  if (c.beacon_type === 'eddystone') return `Eddystone ${String(c.beacon_instance_id || c.beacon_namespace || '').slice(-6) || '?'}`;
  return `Beacon ${String(c.mac_address || c.identifier || '').slice(-8)}`;
};

const buildBeaconPayload = (c, name, purpose) => {
  const display = (name && name.trim()) || candidateDisplayName(c);
  const base = { name: display, asset_name: display, device_type: 'beacon', protocol: 'BLE Beacon', beacon_type: c.beacon_type, beacon_purpose: purpose === 'personal' ? 'personal' : 'asset', allocated_tenant_ids: [] };
  if (c.beacon_type === 'ibeacon') return { ...base, beacon_uuid: c.beacon_uuid || '', beacon_major: String(c.beacon_major ?? ''), beacon_minor: String(c.beacon_minor ?? '') };
  if (c.beacon_type === 'eddystone') return { ...base, beacon_namespace: c.beacon_namespace || '', beacon_instance_id: c.beacon_instance_id || '' };
  return { ...base, mac_address: c.mac_address || c.identifier || '' };
};

const beaconTypeLabel = (t) => (t === 'ibeacon' ? 'iBeacon' : t === 'eddystone' ? 'Eddystone' : 'Vendor / MAC');

const StatTile = ({ label, value, tone = 'default', testid }) => {
  const toneMap = {
    default: 'text-foreground',
    good: 'text-green-600',
    warn: 'text-amber-600',
    bad: 'text-red-600',
  };
  return (
    <div className="rounded-md border border-border p-3" data-testid={testid}>
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className={`mt-1 block text-2xl font-semibold ${toneMap[tone]}`}>{value}</span>
    </div>
  );
};

const dwellTone = (k) => (k === 'persistent'
  ? 'bg-green-50 text-green-700 border-green-200'
  : k === 'occasional'
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-slate-100 text-slate-600 border-slate-200');
const dwellLabel = (k) => (k === 'persistent' ? 'Persistent' : k === 'occasional' ? 'Occasional' : 'Passer-by');
const DWELL_RANK = { persistent: 3, occasional: 2, passerby: 1 };
const FOLLOW_RANK = { high: 3, medium: 2, low: 1 };

// "Following a device" — colour by how strongly an unregistered beacon co-moves with a device
// (high = likely a hidden/attached tag riding along; treat as a security signal).
const followTone = (c) => (c === 'high'
  ? 'bg-red-50 text-red-700 border-red-300'
  : c === 'medium'
    ? 'bg-amber-50 text-amber-700 border-amber-300'
    : 'bg-blue-50 text-blue-700 border-blue-200');


export const BeaconManagement = () => {
  const { api } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const { setPendingDeviceAction } = useAdminPanel();
  const navigate = useNavigate();
  const diagnosticsRef = useRef(null);

  // ── Controls ─────────────────────────────────────────────────
  const [staleDays, setStaleDays] = useState(7);
  const [batteryThreshold, setBatteryThreshold] = useState(20);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // ── Fleet health ─────────────────────────────────────────────
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // ── AVL reporting (server-side searchable table) ─────────────
  const [repRows, setRepRows] = useState([]);
  const [repTotal, setRepTotal] = useState(0);
  const [repPage, setRepPage] = useState(1);
  const [repPageSize, setRepPageSize] = usePersistedPageSize('beacon-reporting', 5);
  const [repSearchInput, setRepSearchInput] = useState('');
  const [repSearch, setRepSearch] = useState('');
  const [repStatus, setRepStatus] = useState('all');
  const [repLoading, setRepLoading] = useState(false);

  // ── Beacon inventory (server-side searchable table) ──────────
  const [invRows, setInvRows] = useState([]);
  const [invTotal, setInvTotal] = useState(0);
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = usePersistedPageSize('beacon-inventory', 5);
  const [invSearchInput, setInvSearchInput] = useState('');
  const [invSearch, setInvSearch] = useState('');
  const [invType, setInvType] = useState('all');
  const [invPurpose, setInvPurpose] = useState('all');
  const [invLoading, setInvLoading] = useState(false);
  const [invExporting, setInvExporting] = useState(false);
  const [invThresholds, setInvThresholds] = useState(null);
  const [deviceBeacons, setDeviceBeacons] = useState({ open: false, imei: '', name: '' });

  // Scan Health (populated-vs-empty beacon records + per-beacon catch rate)
  const [shHours, setShHours] = useState(24);
  const [shData, setShData] = useState(null);
  const [shLoading, setShLoading] = useState(false);

  // ── Auto-detect (unregistered) ───────────────────────────────
  const [discCandidates, setDiscCandidates] = useState([]);
  const [discPage, setDiscPage] = useState(1);
  const [discPageSize, setDiscPageSize] = usePersistedPageSize('beacon-discover', 5);
  const [discLoading, setDiscLoading] = useState(false);
  const [discError, setDiscError] = useState('');
  const [discSearch, setDiscSearch] = useState('');
  const [discType, setDiscType] = useState('all');
  const [discDetector, setDiscDetector] = useState('all');
  const [discMinRssi, setDiscMinRssi] = useState('any');
  const [discMinScans, setDiscMinScans] = useState('0');
  const [discSort, setDiscSort] = useState('recent');
  const [discDwell, setDiscDwell] = useState('all');
  const [discFollowing, setDiscFollowing] = useState('all');
  const [discDetectorOpen, setDiscDetectorOpen] = useState(false);
  const [discDetectorTerm, setDiscDetectorTerm] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkEdits, setBulkEdits] = useState({});
  const [bulkList, setBulkList] = useState([]);
  const [bulkStatus, setBulkStatus] = useState({});

  // ── Per-beacon diagnostics ───────────────────────────────────
  const [beacons, setBeacons] = useState([]);
  const [beaconsLoading, setBeaconsLoading] = useState(false);
  const [selectedBeaconId, setSelectedBeaconId] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagDeviceFilter, setDiagDeviceFilter] = useState('all');
  const [diagWindow, setDiagWindow] = useState('48');
  const [diagSearch, setDiagSearch] = useState('');
  const [diagPage, setDiagPage] = useState(1);
  const [diagPageSize, setDiagPageSize] = usePersistedPageSize('beacon-diagnostics-records', 5);
  const [beaconSearchOpen, setBeaconSearchOpen] = useState(false);
  const [beaconSearchTerm, setBeaconSearchTerm] = useState('');

  const beaconLabel = (b) => b?.asset_name || b?.name || b?.imei || b?.id;

  // ── Fetchers ─────────────────────────────────────────────────
  const fetchHealth = useCallback(async () => {
    if (!api) return;
    setHealthLoading(true);
    try {
      const res = await api.get(`/parsing/beacons/health-summary?stale_days=${staleDays}&battery_threshold=${batteryThreshold}`);
      setHealth(res.data);
    } catch (e) {
      console.error('Failed to load beacon health summary:', e);
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  }, [api, staleDays, batteryThreshold]);

  const fetchReporting = useCallback(async () => {
    if (!api) return;
    setRepLoading(true);
    try {
      const params = new URLSearchParams({
        window_days: String(staleDays),
        search: repSearch,
        status: repStatus,
        page: String(repPage),
        limit: String(repPageSize),
      });
      const res = await api.get(`/parsing/beacons/avl-reporting?${params}`);
      setRepRows(res.data?.rows || []);
      setRepTotal(res.data?.total || 0);
    } catch (e) {
      console.error('Failed to load AVL reporting trackers:', e);
      setRepRows([]);
      setRepTotal(0);
    } finally {
      setRepLoading(false);
    }
  }, [api, staleDays, repSearch, repStatus, repPage, repPageSize]);

  const fetchScanHealth = useCallback(async () => {
    if (!api) return;
    setShLoading(true);
    try {
      const res = await api.get(`/parsing/beacons/scan-health?hours=${shHours}`);
      setShData(res.data);
    } catch (e) {
      console.error('Failed to load scan health:', e);
      setShData(null);
    } finally {
      setShLoading(false);
    }
  }, [api, shHours]);

  const fetchInventory = useCallback(async () => {
    if (!api) return;
    setInvLoading(true);
    try {
      const params = new URLSearchParams({
        search: invSearch,
        beacon_type: invType,
        purpose: invPurpose,
        page: String(invPage),
        limit: String(invPageSize),
      });
      const res = await api.get(`/parsing/beacons/inventory?${params}`);
      setInvRows(res.data?.rows || []);
      setInvTotal(res.data?.total || 0);
      if (res.data?.thresholds) setInvThresholds(res.data.thresholds);
    } catch (e) {
      console.error('Failed to load beacon inventory:', e);
      setInvRows([]);
      setInvTotal(0);
    } finally {
      setInvLoading(false);
    }
  }, [api, invSearch, invType, invPurpose, invPage, invPageSize]);

  const fetchDiscover = useCallback(async () => {
    if (!api) return;
    setDiscLoading(true);
    setDiscError('');
    try {
      const res = await api.get('/parsing/beacons/discover-unregistered?limit=200');
      setDiscCandidates(res.data?.candidates || []);
    } catch (e) {
      setDiscError(e?.response?.data?.detail || 'Failed to load recent BLE scans');
      setDiscCandidates([]);
    } finally {
      setDiscLoading(false);
    }
  }, [api]);

  const fetchBeacons = useCallback(async () => {
    if (!api) return;
    setBeaconsLoading(true);
    try {
      const res = await api.get('/admin/devices?limit=1000');
      const list = (res.data || []).filter((d) => d.device_type === 'beacon');
      list.sort((a, b) => beaconLabel(a).localeCompare(beaconLabel(b)));
      setBeacons(list);
    } catch (e) {
      console.error('Failed to load beacons:', e);
      setBeacons([]);
    } finally {
      setBeaconsLoading(false);
    }
  }, [api]);

  const fetchDiagnostics = useCallback(async (beaconId, deviceImei, windowHours) => {
    if (!api || !beaconId) {
      setDiagnostics(null);
      return;
    }
    setDiagnosticsLoading(true);
    try {
      const p = new URLSearchParams();
      if (deviceImei && deviceImei !== 'all') p.set('device_imei', deviceImei);
      p.set('window_hours', windowHours || '48');
      const res = await api.get(`/parsing/beacons/${encodeURIComponent(beaconId)}/diagnostics?${p.toString()}`);
      setDiagnostics(res.data);
    } catch (e) {
      console.error('Failed to load beacon diagnostics:', e);
      setDiagnostics(null);
    } finally {
      setDiagnosticsLoading(false);
    }
  }, [api]);

  // ── Effects ──────────────────────────────────────────────────
  useEffect(() => { fetchHealth(); }, [fetchHealth]);
  useEffect(() => { fetchReporting(); }, [fetchReporting]);
  useEffect(() => { fetchScanHealth(); }, [fetchScanHealth]);
  useEffect(() => { fetchInventory(); }, [fetchInventory]);
  useEffect(() => { fetchDiscover(); }, [fetchDiscover]);
  useEffect(() => { fetchBeacons(); }, [fetchBeacons]);
  useEffect(() => {
    if (selectedBeaconId) fetchDiagnostics(selectedBeaconId, diagDeviceFilter, diagWindow);
    else setDiagnostics(null);
  }, [selectedBeaconId, diagDeviceFilter, diagWindow, fetchDiagnostics]);

  // Debounce search inputs → reset to page 1.
  useEffect(() => {
    const t = setTimeout(() => { setRepPage(1); setRepSearch(repSearchInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [repSearchInput]);
  useEffect(() => {
    const t = setTimeout(() => { setInvPage(1); setInvSearch(invSearchInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [invSearchInput]);

  // Auto-refresh (~60s).
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      fetchHealth();
      fetchReporting();
      fetchInventory();
      fetchDiscover();
      fetchScanHealth();
    }, 60000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchHealth, fetchReporting, fetchInventory, fetchDiscover, fetchScanHealth]);

  // Live RSSI meter: while auto-refresh is on, refresh ONLY the (paged) inventory every 5s so the
  // signal bars update near-real-time for calibration without hammering the heavier endpoints.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => { fetchInventory(); }, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchInventory]);

  // ── Helpers ──────────────────────────────────────────────────
  const selectBeacon = (id) => {
    setSelectedBeaconId(id);
    setDiagDeviceFilter('all');
    setDiagSearch('');
    setTimeout(() => diagnosticsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const openDeviceBeacons = (imei, name) => {
    if (!imei) return;
    setDeviceBeacons({ open: true, imei, name: name || imei });
  };

  const openBeaconDiagnosticsFromModal = (beaconId) => {
    setDeviceBeacons((s) => ({ ...s, open: false }));
    selectBeacon(beaconId);
  };

  const goRegisterInDeviceManagement = () => {
    setPendingDeviceAction({ action: 'add-beacon', openDiscover: true });
    navigate('/admin-panel?tab=devices');
  };

  const registerCandidate = (cand) => {
    setPendingDeviceAction({ action: 'add-beacon', candidate: cand });
    navigate('/admin-panel?tab=devices');
  };

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const openBulkConfirm = () => {
    const sel = discCandidates.filter((c) => selectedKeys.has(c.identifier));
    const init = {};
    sel.forEach((c) => { init[c.identifier] = { name: candidateDisplayName(c), purpose: 'asset' }; });
    setBulkEdits(init);
    setBulkList(sel);
    setBulkStatus({});
    setBulkDone(false);
    setBulkConfirmOpen(true);
  };

  const updateBulkEdit = (key, field, value) => {
    setBulkEdits((prev) => ({ ...prev, [key]: { ...(prev[key] || { name: '', purpose: 'asset' }), [field]: value } }));
  };

  const closeBulk = () => {
    if (bulkRunning) return;
    setBulkConfirmOpen(false);
    setSelectedKeys(new Set());
    setBulkList([]);
    setBulkStatus({});
    setBulkDone(false);
  };

  // Sequentially register the given candidates, updating per-row status live so admins can
  // watch a large batch progress and see exactly which ones failed.
  const runBulkRegister = async (candidates) => {
    if (!candidates.length) return;
    setBulkRunning(true);
    setBulkDone(false);
    setBulkStatus((prev) => {
      const next = { ...prev };
      candidates.forEach((c) => { next[c.identifier] = { state: 'pending' }; });
      return next;
    });
    let ok = 0, dup = 0, fail = 0;
    for (const c of candidates) {
      setBulkStatus((prev) => ({ ...prev, [c.identifier]: { state: 'registering' } }));
      try {
        const edit = bulkEdits[c.identifier] || {};
        await api.post('/admin/devices', buildBeaconPayload(c, edit.name, edit.purpose));
        ok += 1;
        setBulkStatus((prev) => ({ ...prev, [c.identifier]: { state: 'done' } }));
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : 'Registration failed';
        if (typeof detail === 'string' && detail.toLowerCase().includes('already exists')) {
          dup += 1;
          setBulkStatus((prev) => ({ ...prev, [c.identifier]: { state: 'skipped', reason: 'Already registered' } }));
        } else {
          fail += 1;
          setBulkStatus((prev) => ({ ...prev, [c.identifier]: { state: 'failed', reason: msg } }));
        }
      }
    }
    setBulkRunning(false);
    setBulkDone(true);
    if (ok) toast.success(`Registered ${ok} beacon${ok === 1 ? '' : 's'}${dup ? `, ${dup} skipped` : ''}${fail ? `, ${fail} failed` : ''}`);
    else if (dup && !fail) toast.info(`All ${dup} selected beacon(s) were already registered`);
    else toast.error(`Could not register beacons${fail ? ` — ${fail} failed (check your add-device permission)` : ''}`);
    fetchDiscover();
    fetchInventory();
    fetchHealth();
    fetchBeacons();
  };

  const retryFailed = () => {
    const failed = bulkList.filter((c) => bulkStatus[c.identifier]?.state === 'failed');
    if (failed.length) runBulkRegister(failed);
  };

  const exportStale = () => {
    const list = health?.stale_beacons || [];
    if (!list.length) { toast.info('No stale beacons to export'); return; }
    downloadCsv(
      `beacon_stale_${getFileTimestamp()}.csv`,
      ['Beacon', 'Type', 'Purpose', 'Last Seen', 'Days Since'],
      list.map((b) => [b.name, b.beacon_type || '', b.beacon_purpose || '', b.last_seen_at ? formatDT(b.last_seen_at, { seconds: true }) : 'Never', b.days_since ?? 'Never']),
    );
    toast.success(`Exported ${list.length} beacon(s)`);
  };

  const exportLowBattery = () => {
    const list = health?.low_battery_beacons || [];
    if (!list.length) { toast.info('No low-battery beacons to export'); return; }
    downloadCsv(
      `beacon_low_battery_${getFileTimestamp()}.csv`,
      ['Beacon', 'Battery %', 'Last Seen'],
      list.map((b) => [b.name, b.last_battery_percent ?? (b.low_battery_flag ? 'Low' : ''), b.last_seen_at ? formatDT(b.last_seen_at, { seconds: true }) : '']),
    );
    toast.success(`Exported ${list.length} beacon(s)`);
  };

  const exportReporting = () => {
    if (!repRows.length) { toast.info('No reporting devices to export'); return; }
    downloadCsv(
      `avl_reporting_${getFileTimestamp()}.csv`,
      ['Device', 'IMEI', 'Status', 'Last AVL Report', 'Registered Beacons', 'Beacons Detected'],
      repRows.map((r) => [r.name, r.imei, r.status || '', r.last_report_at ? formatDT(r.last_report_at, { seconds: true }) : '', r.registered_beacon_count ?? 0, r.beacon_count ?? 0]),
    );
    toast.success(`Exported ${repRows.length} device(s)`);
  };

  const exportInventory = async () => {
    if (!invRows.length) { toast.info('No beacons to export'); return; }
    setInvExporting(true);
    const tId = toast.loading('Resolving addresses…');
    try {
      const wantGoogle = isFeatureEnabled('google_reverse_geocoding') && !!GOOGLE_MAPS_API_KEY;
      const points = invRows.map((b) => (b.last_location
        ? { lat: b.last_location.latitude, lng: b.last_location.longitude }
        : null));
      const addresses = await reverseGeocodeBatch(points, { useGoogle: wantGoogle, nominatimBaseUrl: NOMINATIM_BASE });
      downloadCsv(
        `beacon_inventory_${getFileTimestamp()}.csv`,
        ['Beacon', 'Type', 'Purpose', 'Last Seen', 'Battery %', 'RSSI', 'Detecting Device', 'Status', 'Coordinates', 'Address'],
        invRows.map((b, i) => [
          b.name,
          b.beacon_type || '',
          b.beacon_purpose || '',
          b.last_seen_at ? formatDT(b.last_seen_at, { seconds: true }) : 'Never',
          b.last_battery_percent ?? '',
          b.last_rssi ?? '',
          b.detecting_device || '',
          b.binding_status ? `${b.binding_status.label}: ${b.binding_status.reason}` : '',
          b.last_location ? `${b.last_location.latitude}, ${b.last_location.longitude}` : '',
          addresses[i] || '',
        ]),
      );
      toast.success(`Exported ${invRows.length} beacon(s) with addresses`, { id: tId });
    } catch (e) {
      console.error('Inventory export failed:', e);
      toast.error('Export failed', { id: tId });
    } finally {
      setInvExporting(false);
    }
  };

  // Discover client-side filtering (reuses the Add/Edit dialog UX).
  const discDetectorOptions = (() => {
    const names = new Set();
    discCandidates.forEach((c) => (c.detected_by || []).forEach((d) => { if (d.name) names.add(d.name); }));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  })();

  const filteredDiscover = (() => {
    const q = discSearch.trim().toLowerCase();
    const minScans = parseInt(discMinScans, 10);
    const rssiThreshold = discMinRssi === 'any' ? null : parseInt(discMinRssi, 10);
    const list = discCandidates.filter((c) => {
      if (discType !== 'all' && c.beacon_type !== discType) return false;
      if (discDetector !== 'all' && !(c.detected_by || []).some((d) => d.name === discDetector)) return false;
      if (rssiThreshold !== null && !(typeof c.best_rssi === 'number' && c.best_rssi >= rssiThreshold)) return false;
      if (!isNaN(minScans) && minScans > 0 && (c.scan_count || 0) < minScans) return false;
      if (discDwell !== 'all' && (c.dwell?.class || 'passerby') !== discDwell) return false;
      if (discFollowing === 'following' && !c.following) return false;
      if (discFollowing === 'high' && c.following?.confidence !== 'high') return false;
      if (q) {
        const hay = `${c.identifier || ''} ${(c.detected_by || []).map((d) => d.name).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return list.sort((a, b) => {
      if (discSort === 'following') {
        const ra = FOLLOW_RANK[a.following?.confidence] || 0;
        const rb = FOLLOW_RANK[b.following?.confidence] || 0;
        if (rb !== ra) return rb - ra;
        return (b.following?.distance_km || 0) - (a.following?.distance_km || 0);
      }
      if (discSort === 'persistent') {
        const ra = DWELL_RANK[a.dwell?.class] || 0;
        const rb = DWELL_RANK[b.dwell?.class] || 0;
        if (rb !== ra) return rb - ra;
        return (b.dwell?.total_scans || 0) - (a.dwell?.total_scans || 0);
      }
      if (discSort === 'strength') return (b.best_rssi ?? -999) - (a.best_rssi ?? -999);
      if (discSort === 'scans') return (b.scan_count || 0) - (a.scan_count || 0);
      return (b.last_seen || '').localeCompare(a.last_seen || '');
    });
  })();

  // Reset the Auto-detect page back to 1 whenever filters change so
  // the user always sees the first matches.
  useEffect(() => {
    setDiscPage(1);
  }, [discSearch, discType, discDetector, discMinRssi, discMinScans, discSort, discDwell, discFollowing, discPageSize]);

  // Reset the AVL Diagnostics records pagination on filter / selection changes.
  useEffect(() => {
    setDiagPage(1);
  }, [selectedBeaconId, diagDeviceFilter, diagSearch, diagPageSize]);

  const selectedCandidates = discCandidates.filter((c) => selectedKeys.has(c.identifier));
  const allFilteredSelected = filteredDiscover.length > 0 && filteredDiscover.every((c) => selectedKeys.has(c.identifier));
  const toggleSelectAllFiltered = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredDiscover.forEach((c) => next.delete(c.identifier));
      else filteredDiscover.forEach((c) => next.add(c.identifier));
      return next;
    });
  };

  const bulkCounts = bulkList.reduce((acc, c) => {
    const s = bulkStatus[c.identifier]?.state;
    if (s === 'done') acc.done += 1;
    else if (s === 'skipped') acc.skipped += 1;
    else if (s === 'failed') acc.failed += 1;
    return acc;
  }, { done: 0, skipped: 0, failed: 0 });
  const bulkProcessed = bulkCounts.done + bulkCounts.skipped + bulkCounts.failed;
  const bulkProgressPct = bulkList.length ? Math.round((bulkProcessed / bulkList.length) * 100) : 0;

  const totals = health?.totals || {};
  const avl = health?.avl_trackers || {};
  const selectedBeacon = beacons.find((b) => b.id === selectedBeaconId);
  const diagDevices = diagnostics?.teltonika_avl?.detecting_devices || [];
  const diagRecords = (diagnostics?.teltonika_avl?.recent_records || []).filter((rec) => {
    if (!diagSearch.trim()) return true;
    const q = diagSearch.trim().toLowerCase();
    return `${rec.device_name || ''} ${rec.device_imei || ''} ${(rec.avl_ids || []).join(' ')}`.toLowerCase().includes(q);
  });

  return (
    <div className="flex flex-col gap-6" data-testid="beacon-management-page">
      {/* Page header — matches User Management / Device Management styling:
          rounded blue-tinted icon box, bold 2xl title, muted description. */}
      <div className="flex items-center justify-between gap-3">
        <div className="hidden lg:flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2563eb]/10">
            <Radio className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="beacon-management-title">Beacon Management</h1>
            <p className="text-sm text-muted-foreground">Configure the binding engine and monitor BLE beacon health across the fleet</p>
          </div>
        </div>
        <BeaconsHelpPanel />
      </div>

      {/* ── Binding Engine Settings (Phase 3B) ──────────────────── */}
      <BindingEngineSettingsWorkspace api={api} />

      {/* ── Fleet Beacon Health ─────────────────────────────────── */}
      <Card data-testid="beacon-health-card">
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Radio className="w-5 h-5 text-[#2563eb]" /> Fleet Beacon Health
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Diagnostics hub across all your BLE beacons and detecting GPS trackers.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex items-center gap-2 pb-1" data-testid="beacon-auto-refresh-wrap">
                <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} data-testid="beacon-auto-refresh-toggle" />
                <span className="text-xs text-muted-foreground">Auto-refresh (60s)</span>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Not detected in</label>
                <Select value={String(staleDays)} onValueChange={(v) => setStaleDays(Number(v))}>
                  <SelectTrigger className="h-8 w-[130px]" data-testid="beacon-health-stale-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STALE_OPTIONS.map((d) => (
                      <SelectItem key={d} value={String(d)} data-testid={`stale-option-${d}`}>{d} {d === 1 ? 'day' : 'days'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Low battery below</label>
                <Select value={String(batteryThreshold)} onValueChange={(v) => setBatteryThreshold(Number(v))}>
                  <SelectTrigger className="h-8 w-[120px]" data-testid="beacon-health-battery-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BATTERY_OPTIONS.map((p) => (
                      <SelectItem key={p} value={String(p)} data-testid={`battery-option-${p}`}>{p}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={fetchHealth} data-testid="beacon-health-refresh-btn" className="gap-2">
                <RefreshCw className={`w-4 h-4 ${healthLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {health ? (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <StatTile label="Total Beacons" value={totals.beacons ?? 0} testid="health-stat-total" />
                <StatTile label="Detected" value={totals.detected ?? 0} tone="good" testid="health-stat-detected" />
                <StatTile label="Never Detected" value={totals.never_detected ?? 0} tone={(totals.never_detected ?? 0) > 0 ? 'warn' : 'default'} testid="health-stat-never" />
                <StatTile label={`Stale (>${health.stale_days}d)`} value={totals.stale ?? 0} tone={(totals.stale ?? 0) > 0 ? 'warn' : 'good'} testid="health-stat-stale" />
                <StatTile label="Low Battery" value={totals.low_battery ?? 0} tone={(totals.low_battery ?? 0) > 0 ? 'bad' : 'good'} testid="health-stat-lowbatt" />
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Stale beacons */}
                <div className="rounded-md border border-border overflow-hidden" data-testid="beacon-health-stale-table">
                  <div className="flex items-center justify-between gap-2 bg-muted/50 px-3 py-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Clock className="w-4 h-4 text-amber-600" /> Not detected in {health.stale_days} {health.stale_days === 1 ? 'day' : 'days'}
                    </span>
                    <Button variant="outline" size="sm" className="text-xs gap-1.5 h-7 px-3 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={exportStale} data-testid="beacon-stale-export-btn">
                      <Upload className="w-3.5 h-3.5" /> Export CSV
                    </Button>
                  </div>
                  {(health.stale_beacons || []).length > 0 ? (
                    <div className="max-h-[320px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="text-xs">
                            <TableHead className="px-3">Beacon</TableHead>
                            <TableHead className="px-3">Type</TableHead>
                            <TableHead className="px-3">Last Seen</TableHead>
                            <TableHead className="px-3">Age</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {health.stale_beacons.map((b, i) => (
                            <TableRow key={b.id} className="text-xs cursor-pointer" onClick={() => selectBeacon(b.id)} data-testid={`stale-beacon-row-${i}`}>
                              <TableCell className="px-3 font-medium text-[#2563eb] hover:underline">{b.name}</TableCell>
                              <TableCell className="px-3 capitalize">{b.beacon_type || '-'}</TableCell>
                              <TableCell className="px-3">{b.last_seen_at ? formatDT(b.last_seen_at, { seconds: true }) : <span className="text-muted-foreground">Never</span>}</TableCell>
                              <TableCell className="px-3">
                                {b.days_since != null ? <Badge variant="outline" className="bg-amber-100 text-amber-700">{b.days_since}d ago</Badge> : <Badge variant="outline" className="bg-red-100 text-red-700">Never</Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-muted-foreground" data-testid="beacon-health-stale-empty">
                      <CheckCircle2 className="w-4 h-4 text-green-600" /> All beacons detected recently
                    </div>
                  )}
                </div>

                {/* Low battery */}
                <div className="rounded-md border border-border overflow-hidden" data-testid="beacon-health-lowbatt-table">
                  <div className="flex items-center justify-between gap-2 bg-muted/50 px-3 py-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <BatteryWarning className="w-4 h-4 text-red-600" /> Low battery (&lt; {health.battery_threshold_percent}%)
                    </span>
                    <Button variant="outline" size="sm" className="text-xs gap-1.5 h-7 px-3 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={exportLowBattery} data-testid="beacon-lowbatt-export-btn">
                      <Upload className="w-3.5 h-3.5" /> Export CSV
                    </Button>
                  </div>
                  {(health.low_battery_beacons || []).length > 0 ? (
                    <div className="max-h-[320px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="text-xs">
                            <TableHead className="px-3">Beacon</TableHead>
                            <TableHead className="px-3">Battery</TableHead>
                            <TableHead className="px-3">Last Seen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {health.low_battery_beacons.map((b, i) => (
                            <TableRow key={b.id} className="text-xs cursor-pointer" onClick={() => selectBeacon(b.id)} data-testid={`lowbatt-beacon-row-${i}`}>
                              <TableCell className="px-3 font-medium text-[#2563eb] hover:underline">{b.name}</TableCell>
                              <TableCell className="px-3">
                                <Badge variant="outline" className="bg-red-100 text-red-700" title={b.last_battery_mv != null ? `${b.last_battery_mv} mV` : undefined}>
                                  {b.last_battery_percent != null ? `${b.battery_estimated ? '~' : ''}${b.last_battery_percent}%` : (b.low_battery_flag ? 'Low' : '-')}
                                  {b.last_battery_mv != null ? ` · ${b.last_battery_mv} mV` : ''}
                                </Badge>
                              </TableCell>
                              <TableCell className="px-3">{b.last_seen_at ? formatDT(b.last_seen_at, { seconds: true }) : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-muted-foreground" data-testid="beacon-health-lowbatt-empty">
                      <CheckCircle2 className="w-4 h-4 text-green-600" /> No low-battery beacons
                    </div>
                  )}
                </div>
              </div>

              {/* AVL reporting — searchable table */}
              <div className="rounded-md border border-border overflow-hidden" data-testid="beacon-health-avl-coverage">
                <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/50 px-3 py-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-green-700">
                    <SatelliteDish className="w-4 h-4" /> AVL 385/548 reporting devices ({avl.reporting_count ?? repTotal})
                    <span className="font-normal text-muted-foreground">· last {health.stale_days} {health.stale_days === 1 ? 'day' : 'days'}</span>
                  </span>
                  <Button variant="outline" size="sm" className="text-xs gap-1.5 h-7 px-3 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={exportReporting} data-testid="beacon-reporting-export-btn">
                    <Upload className="w-3.5 h-3.5" /> Export CSV
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2 p-3">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input className="pl-8 pr-8 h-8 text-xs" placeholder="Search device name or IMEI…" value={repSearchInput} onChange={(e) => setRepSearchInput(e.target.value)} data-testid="beacon-reporting-search" />
                    {repSearchInput && (
                      <button type="button" onClick={() => setRepSearchInput('')} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground" aria-label="Clear search" data-testid="beacon-reporting-search-clear">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Select value={repStatus} onValueChange={(v) => { setRepStatus(v); setRepPage(1); }}>
                    <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="beacon-reporting-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {repLoading ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground" data-testid="beacon-reporting-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading reporting devices…
                  </div>
                ) : repRows.length > 0 ? (
                  <>
                    <div>
                      <Table>
                        <TableHeader>
                          <TableRow className="text-xs">
                            <TableHead className="px-3">Device</TableHead>
                            <TableHead className="px-3">IMEI</TableHead>
                            <TableHead className="px-3">Status</TableHead>
                            <TableHead className="px-3">Last AVL Report</TableHead>
                            <TableHead className="px-3 text-right" title="Registered / total detected">Beacons</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {repRows.map((r, i) => (
                            <TableRow key={r.id || r.imei} className="text-xs cursor-pointer hover:bg-muted/40" onClick={() => openDeviceBeacons(r.imei, r.name)} data-testid={`reporting-row-${i}`}>
                              <TableCell className="px-3 font-medium text-[#2563eb] hover:underline">{r.name}</TableCell>
                              <TableCell className="px-3 font-mono">{r.imei}</TableCell>
                              <TableCell className="px-3">
                                <Badge variant="outline" className={r.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}>{r.status || '-'}</Badge>
                              </TableCell>
                              <TableCell className="px-3">{r.last_report_at ? formatDT(r.last_report_at, { seconds: true }) : '-'}</TableCell>
                              <TableCell className="px-3 text-right" data-testid={`reporting-beacons-${i}`} title="Registered / total detected">
                                <span className="font-medium text-[#2563eb]">{r.registered_beacon_count ?? 0}</span>
                                <span className="text-muted-foreground"> / {r.beacon_count ?? 0}</span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <PaginationFooter
                      page={repPage}
                      setPage={setRepPage}
                      totalItems={repTotal}
                      itemsPerPage={repPageSize}
                      setItemsPerPage={setRepPageSize}
                      testIdPrefix="beacon-reporting"
                      disabled={repLoading}
                    />
                  </>
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="beacon-reporting-empty">
                    No trackers reporting beacon AVL data in this window.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground" data-testid="beacon-health-empty">
              {healthLoading ? 'Loading beacon health…' : 'Beacon health summary unavailable.'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Scan Health ────────────────────────────────────────── */}
      <Card data-testid="scan-health-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Activity className="w-5 h-5 text-[#2563eb]" /> Scan Health</CardTitle>
              <p className="text-sm text-muted-foreground">
                Populated vs <span className="font-medium">empty</span> beacon records per tracker (an empty record means the scanner reported on schedule but found nothing — a degraded-scanner symptom), plus per-beacon catch rate.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(shHours)} onValueChange={(v) => setShHours(Number(v))}>
                <SelectTrigger className="h-8 w-[120px]" data-testid="scan-health-window-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">Last 6 h</SelectItem>
                  <SelectItem value="24">Last 24 h</SelectItem>
                  <SelectItem value="48">Last 48 h</SelectItem>
                  <SelectItem value="168">Last 7 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchScanHealth} data-testid="scan-health-refresh-btn" className="gap-2">
                <RefreshCw className={`w-4 h-4 ${shLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {shLoading && !shData ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground" data-testid="scan-health-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading scan health…
            </div>
          ) : shData ? (
            <>
              {/* Receivers */}
              <div className="rounded-md border border-border overflow-hidden" data-testid="scan-health-receivers-table">
                <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 text-sm font-medium">
                  <SatelliteDish className="w-4 h-4 text-[#2563eb]" /> Trackers — beacon record quality ({(shData.receivers || []).length})
                </div>
                {(shData.receivers || []).length > 0 ? (
                  <div className="max-h-[340px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="px-3">Tracker</TableHead>
                          <TableHead className="px-3 text-right">Beacon records</TableHead>
                          <TableHead className="px-3 text-right">Populated</TableHead>
                          <TableHead className="px-3 text-right">Empty</TableHead>
                          <TableHead className="px-3 text-right">Populated %</TableHead>
                          <TableHead className="px-3">Last populated</TableHead>
                          <TableHead className="px-3">Health</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {shData.receivers.map((r, i) => (
                          <TableRow key={r.imei} className="text-xs" data-testid={`scan-health-receiver-row-${i}`}>
                            <TableCell className="px-3 font-medium">{r.name}<div className="font-mono text-[10px] text-muted-foreground">{r.imei}</div></TableCell>
                            <TableCell className="px-3 text-right">{r.total_records}</TableCell>
                            <TableCell className="px-3 text-right text-green-700">{r.populated_records}</TableCell>
                            <TableCell className={`px-3 text-right ${r.empty_records > 0 ? 'text-amber-700 font-medium' : 'text-muted-foreground'}`}>{r.empty_records}</TableCell>
                            <TableCell className="px-3 text-right font-medium">{r.populated_pct}%</TableCell>
                            <TableCell className="px-3">{r.last_populated_at ? formatDT(r.last_populated_at, { seconds: true }) : <span className="text-muted-foreground">Never</span>}</TableCell>
                            <TableCell className="px-3" data-testid={`scan-health-receiver-health-${i}`}>
                              {r.health === 'good' ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700">Good</Badge>
                              ) : r.health === 'degraded' ? (
                                <Badge variant="outline" className="bg-amber-100 text-amber-700">Degraded</Badge>
                              ) : r.health === 'mostly_empty' ? (
                                <Badge variant="outline" className="bg-red-100 text-red-700">Mostly empty</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-red-100 text-red-700">Silent scanner</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="scan-health-receivers-empty">No beacon records in this window.</div>
                )}
              </div>

              {/* Beacons */}
              <div className="rounded-md border border-border overflow-hidden" data-testid="scan-health-beacons-table">
                <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 text-sm font-medium">
                  <Bluetooth className="w-4 h-4 text-[#2563eb]" /> Registered beacons — catch rate ({(shData.beacons || []).length})
                </div>
                {(shData.beacons || []).length > 0 ? (
                  <div className="max-h-[340px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="px-3">Beacon</TableHead>
                          <TableHead className="px-3">Purpose</TableHead>
                          <TableHead className="px-3 text-right">Sightings</TableHead>
                          <TableHead className="px-3 text-right">Avg RSSI</TableHead>
                          <TableHead className="px-3">Top receiver</TableHead>
                          <TableHead className="px-3 text-right" title="Share of the top receiver's populated beacon records (within the beacon's active span) that contain this beacon">Catch rate</TableHead>
                          <TableHead className="px-3">Last seen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {shData.beacons.map((b, i) => (
                          <TableRow key={b.beacon_device_id} className="text-xs cursor-pointer" onClick={() => selectBeacon(b.beacon_device_id)} data-testid={`scan-health-beacon-row-${i}`}>
                            <TableCell className="px-3 font-medium text-[#2563eb] hover:underline">{b.name}</TableCell>
                            <TableCell className="px-3 capitalize">{b.purpose}</TableCell>
                            <TableCell className="px-3 text-right">{b.sightings}</TableCell>
                            <TableCell className="px-3 text-right">{b.avg_rssi != null ? `${b.avg_rssi} dBm` : '-'}</TableCell>
                            <TableCell className="px-3">{b.top_receiver_name}</TableCell>
                            <TableCell className="px-3 text-right" data-testid={`scan-health-beacon-catch-${i}`}>
                              {b.catch_pct != null ? (
                                <Badge variant="outline" className={b.catch_pct >= 90 ? 'bg-green-50 text-green-700' : b.catch_pct >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{b.catch_pct}%</Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="px-3">{b.last_seen_at ? formatDT(b.last_seen_at, { seconds: true }) : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="scan-health-beacons-empty">No registered-beacon sightings in this window.</div>
                )}
              </div>
            </>
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="scan-health-empty">Scan health unavailable.</div>
          )}
        </CardContent>
      </Card>

      {/* ── Beacon Inventory ────────────────────────────────────── */}
      <Card data-testid="beacon-inventory-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Radio className="w-5 h-5 text-[#2563eb]" /> Beacon Inventory</CardTitle>
              <p className="text-sm text-muted-foreground">All registered BLE beacons with their latest scan summary. Click a beacon to diagnose.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="text-xs gap-1.5 h-7 px-3 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={fetchInventory} disabled={invLoading} data-testid="beacon-inventory-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${invLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" className="text-xs gap-1.5 h-7 px-3 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={exportInventory} disabled={invExporting} data-testid="beacon-inventory-export-btn">
                {invExporting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving…</> : <><Upload className="w-3.5 h-3.5" /> Export CSV</>}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input className="pl-8 pr-8 h-8 text-xs" placeholder="Search beacon name, IMEI or MAC…" value={invSearchInput} onChange={(e) => setInvSearchInput(e.target.value)} data-testid="beacon-inventory-search" />
              {invSearchInput && (
                <button type="button" onClick={() => setInvSearchInput('')} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground" aria-label="Clear search" data-testid="beacon-inventory-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={invType} onValueChange={(v) => { setInvType(v); setInvPage(1); }}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="beacon-inventory-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="ibeacon">iBeacon</SelectItem>
                <SelectItem value="eddystone">Eddystone</SelectItem>
                <SelectItem value="vendor_specific">Vendor / MAC</SelectItem>
              </SelectContent>
            </Select>
            <Select value={invPurpose} onValueChange={(v) => { setInvPurpose(v); setInvPage(1); }}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="beacon-inventory-purpose-filter"><SelectValue placeholder="Purpose" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All purposes</SelectItem>
                <SelectItem value="asset">Asset</SelectItem>
                <SelectItem value="personal">Personal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-md border border-border overflow-hidden">
            {invLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground" data-testid="beacon-inventory-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading beacons…
              </div>
            ) : invRows.length > 0 ? (
              <>
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="px-3">Beacon</TableHead>
                        <TableHead className="px-3">Type</TableHead>
                        <TableHead className="px-3">Purpose</TableHead>
                        <TableHead className="px-3">Last Seen</TableHead>
                        <TableHead className="px-3">Battery</TableHead>
                        <TableHead className="px-3">Signal (live)</TableHead>
                        <TableHead className="px-3">Detecting Device</TableHead>
                        <TableHead className="px-3">Status</TableHead>
                        <TableHead className="px-3">Last Location</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invRows.map((b, i) => (
                        <TableRow key={b.id} className="text-xs cursor-pointer" onClick={() => selectBeacon(b.id)} data-testid={`inventory-row-${i}`}>
                          <TableCell className="px-3 font-medium text-[#2563eb] hover:underline">{b.name}</TableCell>
                          <TableCell className="px-3 capitalize">{b.beacon_type === 'vendor_specific' ? 'Vendor/MAC' : (b.beacon_type || '-')}</TableCell>
                          <TableCell className="px-3 capitalize">{b.beacon_purpose || '-'}</TableCell>
                          <TableCell className="px-3">{b.last_seen_at ? formatDT(b.last_seen_at, { seconds: true }) : <span className="text-muted-foreground">Never</span>}</TableCell>
                          <TableCell className="px-3">
                            {b.last_battery_percent != null
                              ? <Badge variant="outline" title={b.last_battery_mv != null ? `${b.last_battery_mv} mV${b.battery_estimated ? ' (estimated from voltage)' : ''}` : undefined} className={(b.low_battery || b.last_battery_percent <= 20) ? 'bg-red-100 text-red-700' : 'bg-green-50 text-green-700'}>{b.battery_estimated ? '~' : ''}{b.last_battery_percent}%{b.last_battery_mv != null ? ` · ${b.last_battery_mv} mV` : ''}</Badge>
                              : <span className="text-muted-foreground">-</span>}
                          </TableCell>
                          <TableCell className="px-3">
                            <div className="space-y-1.5">
                              {(b.detectors && b.detectors.length ? b.detectors : [null]).map((d, di) => (
                                <div key={di} className="min-h-[44px] flex items-center">
                                  <RssiMeter
                                    rssi={d ? d.rssi : null}
                                    lastSeenAt={d ? d.last_seen_at : null}
                                    level={d ? d.signal_level : null}
                                    thresholds={invThresholds ? invThresholds[b.beacon_purpose === 'personal' ? 'personal' : 'asset'] : undefined}
                                    testid={di === 0 ? `inventory-rssi-meter-${i}` : `inventory-rssi-meter-${i}-${di}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                            {b.detectors && b.detectors.length ? (
                              <div className="space-y-1.5">
                                {b.handover && (
                                  <div className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" data-testid={`inventory-handover-${i}`} title={`Strongest detector changed from ${b.handover.from_device_name} to ${b.handover.to_device_name} within the last 2 minutes`}>
                                    <ArrowLeftRight className="w-3 h-3" /> Handover: {b.handover.from_device_name} → {b.handover.to_device_name}
                                  </div>
                                )}
                                {b.detectors.map((d, di) => (
                                  <div key={di} className="min-h-[44px] flex flex-col justify-center" data-testid={di === 0 ? `inventory-detecting-device-${i}` : `inventory-detecting-device-${i}-${di}`}>
                                    {d.device_imei
                                      ? <span className="text-[#2563eb] hover:underline cursor-pointer text-sm" onClick={() => openDeviceBeacons(d.device_imei, d.device_name)}>{d.device_name || d.device_imei}</span>
                                      : <span className="text-sm">{d.device_name || '-'}</span>}
                                    {di === 0 && b.detectors.length > 1 && <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">closest</span>}
                                  </div>
                                ))}
                              </div>
                            ) : <span className="text-muted-foreground">-</span>}
                          </TableCell>
                          <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                            <BindingStatusCell status={b.binding_status} testId={`inventory-binding-status-${i}`} />
                          </TableCell>
                          <TableCell className="px-3"><LocationPopover loc={b.last_location} testid={`inventory-location-${i}`} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <PaginationFooter
                  page={invPage}
                  setPage={setInvPage}
                  totalItems={invTotal}
                  itemsPerPage={invPageSize}
                  setItemsPerPage={setInvPageSize}
                  testIdPrefix="beacon-inventory"
                  disabled={invLoading}
                />
              </>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="beacon-inventory-empty">
                No beacons match your filters.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Auto-detect Beacons from Recent Scans (rendered last via order-last) ── */}
      <Card data-testid="beacon-discover-card" className="order-last">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><ScanLine className="w-5 h-5 text-[#2563eb]" /> Auto-detect Beacons from Recent Scans</CardTitle>
              <p className="text-sm text-muted-foreground">Unregistered BLE beacons recently detected by your GPS trackers. Register them to start tracking.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={fetchDiscover} disabled={discLoading} data-testid="beacon-discover-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${discLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button size="sm" className="h-8 gap-1 text-xs bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={goRegisterInDeviceManagement} data-testid="beacon-discover-register-btn">
                <Plus className="w-3.5 h-3.5" /> Register in Device Management
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
            <div className="relative col-span-2 md:col-span-1">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input className="pl-8 pr-8 h-8 text-xs" placeholder="Search…" value={discSearch} onChange={(e) => setDiscSearch(e.target.value)} data-testid="beacon-discover-search" />
              {discSearch && (
                <button type="button" onClick={() => setDiscSearch('')} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground" aria-label="Clear search" data-testid="beacon-discover-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={discType} onValueChange={setDiscType}>
              <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="ibeacon">iBeacon</SelectItem>
                <SelectItem value="eddystone">Eddystone</SelectItem>
                <SelectItem value="vendor_specific">Vendor / MAC</SelectItem>
              </SelectContent>
            </Select>
            <Popover open={discDetectorOpen} onOpenChange={(o) => { setDiscDetectorOpen(o); if (!o) setDiscDetectorTerm(''); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full h-8 justify-between text-xs font-normal" data-testid="beacon-discover-detector-filter">
                  <span className="truncate">{discDetector === 'all' ? 'Any device' : discDetector}</span>
                  <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-0 z-[10000]" align="start">
                <Command shouldFilter={false}>
                  <CommandInput placeholder="Search device…" value={discDetectorTerm} onValueChange={setDiscDetectorTerm} data-testid="beacon-discover-detector-search" />
                  <CommandList>
                    <CommandEmpty>No devices found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem value="all" onSelect={() => { setDiscDetector('all'); setDiscDetectorOpen(false); setDiscDetectorTerm(''); }}>Any device</CommandItem>
                      {discDetectorOptions
                        .filter((n) => n.toLowerCase().includes(discDetectorTerm.trim().toLowerCase()))
                        .map((n) => (
                          <CommandItem key={n} value={n} onSelect={() => { setDiscDetector(n); setDiscDetectorOpen(false); setDiscDetectorTerm(''); }}>{n}</CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Select value={discMinRssi} onValueChange={setDiscMinRssi}>
              <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-rssi-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any signal</SelectItem>
                <SelectItem value="-35">≥ -35 dBm (nearest)</SelectItem>
                <SelectItem value="-50">≥ -50 dBm (nearer)</SelectItem>
                <SelectItem value="-70">≥ -70 dBm (near)</SelectItem>
                <SelectItem value="-85">≥ -85 dBm (medium)</SelectItem>
                <SelectItem value="-100">≥ -100 dBm (far)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={discDwell} onValueChange={setDiscDwell}>
              <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-dwell-filter"><SelectValue placeholder="Dwell" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All dwell</SelectItem>
                <SelectItem value="persistent">Persistent only</SelectItem>
                <SelectItem value="occasional">Occasional only</SelectItem>
                <SelectItem value="passerby">Passer-by only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={discFollowing} onValueChange={setDiscFollowing}>
              <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-following-filter"><SelectValue placeholder="Following" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All beacons</SelectItem>
                <SelectItem value="following">Following a device</SelectItem>
                <SelectItem value="high">Following — high only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={discSort} onValueChange={setDiscSort}>
              <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="strength">Strongest signal</SelectItem>
                <SelectItem value="scans">Most scans</SelectItem>
                <SelectItem value="persistent">Persistent first</SelectItem>
                <SelectItem value="following">Following first</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAllFiltered} disabled={filteredDiscover.length === 0} data-testid="beacon-discover-select-all" aria-label="Select all shown beacons" />
              <span className="text-xs text-muted-foreground" data-testid="beacon-discover-count">
                {selectedCandidates.length > 0 ? `${selectedCandidates.length} selected · ` : ''}{filteredDiscover.length} of {discCandidates.length} unregistered beacon(s)
              </span>
            </div>
            <Button size="sm" className="h-8 gap-1 text-xs bg-[#16a34a] hover:bg-[#15803d]" disabled={selectedCandidates.length === 0} onClick={openBulkConfirm} data-testid="beacon-discover-bulk-register-btn">
              <Plus className="w-3.5 h-3.5" /> Register Selected ({selectedCandidates.length})
            </Button>
          </div>
          {discError && (
            <div className="flex items-center gap-3">
              <p className="text-sm text-red-600" data-testid="beacon-discover-error">{discError}</p>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={fetchDiscover} disabled={discLoading} data-testid="beacon-discover-retry-btn">Retry</Button>
            </div>
          )}
          <div className="space-y-2" data-testid="beacon-discover-list">
            {discLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading recent scans…</div>
            ) : discCandidates.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground" data-testid="beacon-discover-empty">No unregistered beacons detected recently.</div>
            ) : filteredDiscover.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground" data-testid="beacon-discover-no-match">No beacons match your filters.</div>
            ) : (
              paginateArray(filteredDiscover, discPage, discPageSize).map((c, idx) => (
                <div key={c.identifier || idx} className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors" data-testid={`beacon-discover-row-${idx}`}>
                  <Checkbox checked={selectedKeys.has(c.identifier)} onCheckedChange={() => toggleSelect(c.identifier)} data-testid={`beacon-discover-select-${idx}`} aria-label="Select beacon" />
                  <div className="min-w-0 space-y-1 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] uppercase">{c.beacon_type === 'ibeacon' ? 'iBeacon' : c.beacon_type === 'eddystone' ? 'Eddystone' : 'Vendor / MAC'}</Badge>
                      {typeof c.best_rssi === 'number' && <span className="text-xs text-muted-foreground flex items-center gap-1"><Signal className="w-3 h-3" /> {c.best_rssi} dBm</span>}
                      {typeof c.battery === 'number' && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1" title={typeof c.battery_voltage_mv === 'number' ? `${c.battery_voltage_mv} mV${c.battery_estimated ? ' (estimated from voltage)' : ''}` : undefined} data-testid={`beacon-discover-battery-${idx}`}>
                          <BatteryMedium className="w-3 h-3" /> {c.battery_estimated ? '~' : ''}{c.battery}%{typeof c.battery_voltage_mv === 'number' ? ` (${c.battery_voltage_mv} mV)` : ''}
                        </span>
                      )}
                      {c.low_battery && <Badge variant="destructive" className="text-[10px]">Low battery</Badge>}
                      {c.dwell && <Badge variant="outline" className={`text-[10px] ${dwellTone(c.dwell.class)}`} title="How persistently this beacon has been seen — Persistent = likely an installed asset, Passer-by = one-off" data-testid={`beacon-discover-dwell-badge-${idx}`}>{dwellLabel(c.dwell.class)}</Badge>}
                      {c.following && (
                        <Badge
                          variant="outline"
                          className={`text-[10px] gap-1 ${followTone(c.following.confidence)}`}
                          title={`This beacon appears to be travelling WITH "${c.following.device_name}" — detected at ${c.following.distinct_locations} distinct locations spanning ~${c.following.distance_km} km. A possibly hidden / attached tag.`}
                          data-testid={`beacon-discover-following-${idx}`}
                        >
                          <Crosshair className="w-3 h-3" />
                          Following: {c.following.device_name} ({c.following.confidence})
                        </Badge>
                      )}
                    </div>
                    <p className="font-mono text-xs font-medium text-foreground break-all">{c.identifier}</p>
                    <p className="text-[11px] text-muted-foreground">
                      Seen {c.last_seen ? formatDT(c.last_seen, { seconds: true }) : '—'}
                      {c.detected_by && c.detected_by.length > 0 && <> · by {c.detected_by.map((d) => d.name).join(', ')}</>}
                      {typeof c.scan_count === 'number' && <> · {c.scan_count} scan{c.scan_count === 1 ? '' : 's'}</>}
                    </p>
                    {c.first_seen && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`beacon-discover-first-seen-${idx}`}>
                        <Flag className="w-3 h-3 text-[#2563eb]" title="First time this beacon was detected" />
                        First seen {formatDT(c.first_seen, { seconds: true })}
                        {c.first_seen_by && c.first_seen_by.name && <> · by {c.first_seen_by.name}</>}
                      </p>
                    )}
                    {c.last_seen_strongest && c.last_seen_strongest.name && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`beacon-discover-last-seen-by-${idx}`}>
                        <Bluetooth className="w-3 h-3 text-[#2563eb]" title="Device that detected this beacon in the most recent scan (strongest signal if several saw it)" />
                        Last seen by {c.last_seen_strongest.name} · {formatDT(c.last_seen_strongest.timestamp || c.last_seen, { seconds: true })}
                        {typeof c.last_seen_strongest.rssi === 'number' && <> · {c.last_seen_strongest.rssi} dBm</>}
                      </p>
                    )}
                    {c.dwell && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`beacon-discover-dwell-${idx}`}>
                        <Activity className="w-3 h-3 text-[#2563eb]" title="First-to-last span and scan frequency" />
                        Dwell: {c.dwell.span_label} span · {c.dwell.total_scans} scan{c.dwell.total_scans === 1 ? '' : 's'}{c.dwell.per_day != null ? ` · ~${c.dwell.per_day}/day` : ''}
                      </p>
                    )}
                    <div className="text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground">First location:</span>
                        <LocationPopover loc={c.first_location} testid={`beacon-discover-first-location-${idx}`} />
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground">Last location:</span>
                        <LocationPopover loc={c.last_location} testid={`beacon-discover-location-${idx}`} />
                      </span>
                    </div>
                  </div>
                  <Button type="button" size="sm" className="h-8 text-xs shrink-0 bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={() => registerCandidate(c)} data-testid={`beacon-discover-register-${idx}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Register
                  </Button>
                </div>
              ))
            )}
          </div>
          <PaginationFooter
            page={discPage}
            setPage={setDiscPage}
            totalItems={filteredDiscover.length}
            itemsPerPage={discPageSize}
            setItemsPerPage={setDiscPageSize}
            testIdPrefix="beacon-discover"
            disabled={discLoading}
          />
          <Dialog open={bulkConfirmOpen} onOpenChange={(o) => { if (!o && !bulkRunning) closeBulk(); }}>
            <DialogContent className="max-w-lg" data-testid="beacon-bulk-confirm-modal">
              <DialogHeader>
                <DialogTitle>
                  {bulkDone ? 'Registration Complete' : `Register ${bulkList.length} Beacon${bulkList.length === 1 ? '' : 's'}?`}
                </DialogTitle>
                <DialogDescription>
                  {bulkDone
                    ? 'Review the results below. You can retry any that failed, or close.'
                    : 'Review the name and purpose for each beacon, then register. Names default to an auto-generated label — edit any you like (a blank name keeps the auto label). You can re-allocate them afterwards.'}
                </DialogDescription>
              </DialogHeader>

              <div className="max-h-[340px] overflow-y-auto rounded-md border divide-y" data-testid="beacon-bulk-confirm-list">
                {bulkList.map((c, i) => {
                  const st = bulkStatus[c.identifier]?.state;
                  const locked = bulkRunning || bulkDone;
                  return (
                    <div key={c.identifier} className="space-y-2 px-3 py-2.5 text-xs" data-testid={`beacon-bulk-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px] uppercase shrink-0">{beaconTypeLabel(c.beacon_type)}</Badge>
                        <span className="font-mono text-muted-foreground truncate">{c.identifier}</span>
                        <span className="ml-auto shrink-0" data-testid={`beacon-bulk-status-${i}`}>
                          {st === 'registering' && <Loader2 className="w-4 h-4 animate-spin text-[#2563eb]" />}
                          {st === 'done' && <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="w-3.5 h-3.5" /> Registered</span>}
                          {st === 'skipped' && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" /> Skipped</span>}
                          {st === 'failed' && <span className="flex items-center gap-1 text-red-600"><XCircle className="w-3.5 h-3.5" /> Failed</span>}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input className="h-8 text-xs flex-1" value={bulkEdits[c.identifier]?.name ?? ''} onChange={(e) => updateBulkEdit(c.identifier, 'name', e.target.value)} placeholder={candidateDisplayName(c)} disabled={locked} data-testid={`beacon-bulk-name-${i}`} />
                        <Select value={bulkEdits[c.identifier]?.purpose || 'asset'} onValueChange={(v) => updateBulkEdit(c.identifier, 'purpose', v)} disabled={locked}>
                          <SelectTrigger className="h-8 w-[120px] text-xs shrink-0" data-testid={`beacon-bulk-purpose-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asset">Asset</SelectItem>
                            <SelectItem value="personal">Personal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {st === 'failed' && bulkStatus[c.identifier]?.reason && (
                        <p className="text-[11px] text-red-600">{bulkStatus[c.identifier].reason}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {(bulkRunning || bulkDone) && (
                <div className="space-y-2" data-testid="beacon-bulk-progress">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-[#16a34a] transition-all duration-300" style={{ width: `${bulkProgressPct}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground" data-testid="beacon-bulk-progress-text">
                      {bulkRunning ? `Registering ${bulkProcessed} of ${bulkList.length}…` : `${bulkProcessed} of ${bulkList.length} processed`}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="text-green-600" data-testid="beacon-bulk-count-done">{bulkCounts.done} registered</span>
                      <span className="text-amber-600" data-testid="beacon-bulk-count-skipped">{bulkCounts.skipped} skipped</span>
                      <span className="text-red-600" data-testid="beacon-bulk-count-failed">{bulkCounts.failed} failed</span>
                    </span>
                  </div>
                </div>
              )}

              <DialogFooter>
                {!bulkDone ? (
                  <>
                    <Button size="sm" variant="outline" onClick={closeBulk} disabled={bulkRunning} data-testid="beacon-bulk-confirm-cancel">Cancel</Button>
                    <Button size="sm" className="bg-[#16a34a] hover:bg-[#15803d]" onClick={() => runBulkRegister(bulkList)} disabled={bulkRunning || bulkList.length === 0} data-testid="beacon-bulk-confirm-submit">
                      {bulkRunning ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Registering…</> : `Confirm & Register (${bulkList.length})`}
                    </Button>
                  </>
                ) : (
                  <>
                    {bulkCounts.failed > 0 && (
                      <Button size="sm" variant="outline" onClick={retryFailed} data-testid="beacon-bulk-retry-failed">
                        <RefreshCw className="w-4 h-4 mr-1" /> Retry failed ({bulkCounts.failed})
                      </Button>
                    )}
                    <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={closeBulk} data-testid="beacon-bulk-close">Done</Button>
                  </>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* ── Per-beacon AVL diagnostics ──────────────────────────── */}
      <Card ref={diagnosticsRef} data-testid="beacon-diagnostics-section">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Activity className="w-5 h-5 text-[#2563eb]" /> Per-Beacon AVL Diagnostics</CardTitle>
          <p className="text-sm text-muted-foreground">
            Records below are <span className="font-medium">beacon-scoped</span> — only GPS trackers that actually detected the selected beacon. Click a beacon in any table above, or pick one here.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[260px]">
              <label className="text-xs text-muted-foreground block mb-1">Select Beacon</label>
              <Popover open={beaconSearchOpen} onOpenChange={(open) => { setBeaconSearchOpen(open); if (!open) setBeaconSearchTerm(''); }}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" data-testid="beacon-management-select" className="w-[280px] h-8 justify-between text-sm font-normal">
                    <span className="truncate">
                      {selectedBeacon ? beaconLabel(selectedBeacon) : <span className="text-muted-foreground">{beaconsLoading ? 'Loading beacons…' : 'Choose a beacon'}</span>}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0 z-[10000]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Search beacon…" value={beaconSearchTerm} onValueChange={setBeaconSearchTerm} data-testid="beacon-management-select-search" />
                    <CommandList>
                      <CommandEmpty>No beacons found.</CommandEmpty>
                      <CommandGroup>
                        {beacons
                          .filter((b) => beaconLabel(b).toLowerCase().includes(beaconSearchTerm.trim().toLowerCase()))
                          .map((b) => (
                            <CommandItem key={b.id} value={b.id} data-testid={`beacon-option-${b.id}`} onSelect={() => { selectBeacon(b.id); setBeaconSearchOpen(false); setBeaconSearchTerm(''); }}>
                              {beaconLabel(b)}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <Button variant="outline" size="sm" onClick={() => { fetchBeacons(); fetchDiagnostics(selectedBeaconId, diagDeviceFilter, diagWindow); }} data-testid="beacon-management-refresh-btn" className="gap-2">
              <RefreshCw className={`w-4 h-4 ${(beaconsLoading || diagnosticsLoading) ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          {beacons.length === 0 && !beaconsLoading && (
            <div className="py-8 text-center text-muted-foreground" data-testid="beacon-management-no-beacons">No BLE beacons are registered for your account yet.</div>
          )}

          {selectedBeaconId && (
            <Card data-testid="beacon-avl-diagnostics-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4" /> Beacon AVL Diagnostics
                    {selectedBeacon ? <span className="font-normal text-muted-foreground">— {beaconLabel(selectedBeacon)}</span> : null}
                  </CardTitle>
                  {diagnosticsLoading && <Loader2 className="w-4 h-4 animate-spin text-[#2563eb]" data-testid="beacon-diagnostics-loading" />}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                {diagnostics ? (
                  <>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <div className="rounded-md border border-border p-3">
                        <span className="block text-muted-foreground">Registered Match Type</span>
                        <span className="mt-1 block font-medium" data-testid="beacon-diagnostics-match-type">{diagnostics.identifier?.match_type || '-'}</span>
                      </div>
                      <div className="rounded-md border border-border p-3 min-w-0">
                        <span className="block text-muted-foreground">Expected Identifier</span>
                        <span className="mt-1 block font-mono break-all" data-testid="beacon-diagnostics-expected-identifier">{diagnostics.identifier?.expected_identifier || '-'}</span>
                      </div>
                      <div className="rounded-md border border-border p-3">
                        <span className="block text-muted-foreground">Matched Scans ({diagWindow === '168' ? '7d' : `${diagWindow}h`})</span>
                        <Badge variant="outline" className={`mt-1 ${diagnostics.matched_scan_count > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="beacon-diagnostics-matched-scan-count">{diagnostics.matched_scan_count || 0}</Badge>
                      </div>
                      <div className="rounded-md border border-border p-3">
                        <span className="block text-muted-foreground">Beacon-scoped AVL 385/548 Records</span>
                        <Badge variant="outline" className={`mt-1 ${(diagnostics.teltonika_avl?.visible_recent_record_count || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`} data-testid="beacon-diagnostics-avl-record-count">{diagnostics.teltonika_avl?.visible_recent_record_count || 0}</Badge>
                      </div>
                    </div>

                    {(diagnostics.hints || []).length > 0 && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30" data-testid="beacon-diagnostics-hints">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                          <div className="space-y-1 text-amber-900 dark:text-amber-200">{diagnostics.hints.map((hint, i) => <p key={i}>{hint}</p>)}</div>
                        </div>
                      </div>
                    )}

                    <div className="rounded-md border border-border overflow-hidden" data-testid="beacon-diagnostics-avl-records">
                      <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/50 px-3 py-2">
                        <span className="font-medium">Recent GPS Tracker Beacon AVL Records (this beacon)</span>
                        <div className="flex items-center gap-2">
                          <Select value={diagWindow} onValueChange={setDiagWindow}>
                            <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="beacon-diagnostics-window-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="12">Last 12 h</SelectItem>
                              <SelectItem value="48">Last 48 h</SelectItem>
                              <SelectItem value="168">Last 7 days</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={diagDeviceFilter} onValueChange={setDiagDeviceFilter}>
                            <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="beacon-diagnostics-device-filter"><SelectValue placeholder="All GPS devices" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All GPS devices</SelectItem>
                              {diagDevices.map((d) => <SelectItem key={d.imei} value={d.imei}>{d.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <div className="relative">
                            <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-muted-foreground" />
                            <Input className="pl-7 pr-7 h-8 w-[160px] text-xs" placeholder="Search records…" value={diagSearch} onChange={(e) => setDiagSearch(e.target.value)} data-testid="beacon-diagnostics-records-search" />
                            {diagSearch && (
                              <button type="button" onClick={() => setDiagSearch('')} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground" aria-label="Clear search" data-testid="beacon-diagnostics-records-search-clear">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {diagRecords.length > 0 ? (
                        <>
                        <div>
                          <Table>
                            <TableHeader>
                              <TableRow className="text-xs">
                                <TableHead className="px-3">GPS Device</TableHead>
                                <TableHead className="px-3">AVL IDs</TableHead>
                                <TableHead className="px-3">RSSI</TableHead>
                                <TableHead className="px-3">Codec</TableHead>
                                <TableHead className="px-3">Time</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginateArray(diagRecords, diagPage, diagPageSize).map((rec, i) => (
                                <TableRow key={`${rec.device_imei}-${rec.timestamp}-${i}`} className="text-xs" data-testid={`beacon-diagnostics-avl-row-${i}`}>
                                  <TableCell className="px-3 font-medium">{rec.device_name || rec.device_imei}</TableCell>
                                  <TableCell className="px-3 font-mono">{(rec.avl_ids || []).join(', ') || '-'}</TableCell>
                                  <TableCell className="px-3" data-testid={`beacon-diagnostics-avl-rssi-${i}`}>
                                    {typeof rec.rssi === 'number'
                                      ? <span className="inline-flex items-center gap-1"><Signal className="w-3 h-3 text-[#2563eb]" />{rec.rssi} dBm</span>
                                      : '-'}
                                  </TableCell>
                                  <TableCell className="px-3">{rec.codec || '-'}</TableCell>
                                  <TableCell className="px-3">{rec.timestamp ? formatDT(rec.timestamp, { seconds: true }) : '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        <PaginationFooter
                          page={diagPage}
                          setPage={setDiagPage}
                          totalItems={diagRecords.length}
                          itemsPerPage={diagPageSize}
                          setItemsPerPage={setDiagPageSize}
                          testIdPrefix="beacon-diagnostics-records"
                        />
                        </>
                      ) : (
                        <div className="px-3 py-4 text-center text-muted-foreground" data-testid="beacon-diagnostics-no-avl-records">
                          <MousePointerClick className="w-4 h-4 inline mr-1 opacity-60" /> No beacon-scoped AVL 385/548 records for this beacon yet
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="py-4 text-center text-muted-foreground" data-testid="beacon-diagnostics-empty">
                    {diagnosticsLoading ? 'Loading diagnostics…' : 'Diagnostics unavailable for this beacon'}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <DeviceBeaconsModal
        open={deviceBeacons.open}
        onOpenChange={(o) => setDeviceBeacons((s) => ({ ...s, open: o }))}
        api={api}
        imei={deviceBeacons.imei}
        deviceName={deviceBeacons.name}
        isFeatureEnabled={isFeatureEnabled}
        onOpenDiagnostics={openBeaconDiagnosticsFromModal}
      />
    </div>
  );
};
